import { z } from 'zod';
import { Priority, WorkPhase, WorkType } from './enums.js';
import { IsoDateTime, UserRef, Uuid, Version, boundedList } from './common.js';
import { CustomFieldValue } from './fields.js';

const ProjectRefShape = z.object({ id: Uuid, key: z.string(), name: z.string() });

/** What starts a rule: a change on a work item in the rule's scope. */
export const AutomationTrigger = z.enum(['WORK_CREATED', 'PHASE_CHANGED', 'PRIORITY_CHANGED', 'ASSIGNEE_CHANGED', 'LABELS_CHANGED', 'DUE_DATE_CHANGED', 'BLOCKED']);
export type AutomationTrigger = z.infer<typeof AutomationTrigger>;

const Label = z.string().trim().min(1).max(40);

/** Every condition given must hold on the item as it is after the change. */
export const AutomationConditions = z
  .object({
    types: boundedList(WorkType, 20).optional(),
    priorities: boundedList(Priority, 4).optional(),
    /** Phase after the change. */
    phases: boundedList(WorkPhase, 6).optional(),
    /** Phase before the change (phase changes only). */
    fromPhases: boundedList(WorkPhase, 6).optional(),
    /** The item has any of these labels. */
    labels: boundedList(Label, 10).optional(),
    /** true: nobody is assigned; false: someone is. */
    unassigned: z.boolean().optional(),
  })
  .strict();
export type AutomationConditions = z.infer<typeof AutomationConditions>;

export const NotifyRecipient = z.enum(['ASSIGNEE', 'OWNER', 'CREATOR', 'FOLLOWERS']);

export const AutomationAction = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('SET_PRIORITY'), priority: Priority }).strict(),
  /** null unassigns. The person must be able to do the work (same check as a manual assignment). */
  z.object({ kind: z.literal('SET_ASSIGNEE'), assigneeId: Uuid.nullable() }).strict(),
  z.object({ kind: z.literal('ADD_LABELS'), labels: boundedList(Label, 10).min(1) }).strict(),
  z.object({ kind: z.literal('REMOVE_LABELS'), labels: boundedList(Label, 10).min(1) }).strict(),
  /** Due date N days after the change (0 = the same day); onlyIfEmpty keeps an existing date. */
  z.object({ kind: z.literal('SET_DUE_IN_DAYS'), days: z.number().int().min(0).max(365), onlyIfEmpty: z.boolean().optional() }).strict(),
  z.object({ kind: z.literal('SET_FIELD'), fieldId: Uuid, value: CustomFieldValue.nullable() }).strict(),
  /** In-app and e-mail notice (per each person's preferences) to roles and named people. */
  z.object({ kind: z.literal('NOTIFY'), recipients: boundedList(NotifyRecipient, 4).optional(), userIds: boundedList(Uuid, 20).optional(), message: z.string().trim().min(1).max(300) }).strict(),
]);
export type AutomationAction = z.infer<typeof AutomationAction>;

export const AutomationRuleView = z.object({
  id: Uuid,
  name: z.string(),
  enabled: z.boolean(),
  /** null: the rule applies to all work. */
  project: ProjectRefShape.nullable(),
  trigger: AutomationTrigger,
  conditions: AutomationConditions,
  actions: z.array(AutomationAction),
  createdBy: UserRef.nullable(),
  runCount: z.number().int(),
  lastRunAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
  archived: z.boolean(),
  version: Version,
  canManage: z.boolean(),
  updatedAt: IsoDateTime,
});
export type AutomationRuleView = z.infer<typeof AutomationRuleView>;

export const CreateAutomationRule = z
  .object({
    name: z.string().trim().min(1).max(80),
    /** Omit for a rule over all work (administrators only). */
    projectId: Uuid.optional(),
    trigger: AutomationTrigger,
    conditions: AutomationConditions.optional(),
    actions: boundedList(AutomationAction, 10).min(1),
    enabled: z.boolean().optional(),
  })
  .strict();
export type CreateAutomationRule = z.infer<typeof CreateAutomationRule>;

export const UpdateAutomationRule = z
  .object({
    expectedVersion: Version,
    name: z.string().trim().min(1).max(80).optional(),
    trigger: AutomationTrigger.optional(),
    conditions: AutomationConditions.optional(),
    actions: boundedList(AutomationAction, 10).min(1).optional(),
    enabled: z.boolean().optional(),
    archived: z.boolean().optional(),
  })
  .strict();
export type UpdateAutomationRule = z.infer<typeof UpdateAutomationRule>;

export const AutomationRunView = z.object({
  id: Uuid,
  /** null when the reader cannot see the work item. */
  workItem: z.object({ id: Uuid, key: z.string(), title: z.string() }).nullable(),
  status: z.enum(['APPLIED', 'FAILED']),
  detail: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type AutomationRunView = z.infer<typeof AutomationRunView>;

export const AutomationRuleListQuery = z.object({
  /** Rules of this project; without it, the rules over all work plus those of projects the caller manages. */
  projectId: Uuid.optional(),
  includeArchived: z.enum(['true']).optional(),
});
